import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations/canvaTranslations';

const SolutionSection = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const mermaidRef = useRef(null);
  const [diagramId] = useState(`solution-diagram-${Math.random().toString(36).substr(2, 9)}`);

  useEffect(() => {
    // Initialize Mermaid
    mermaid.initialize({
      startOnLoad: false,
      theme: 'default',
      themeVariables: {
        primaryColor: '#00C4CC',
        primaryTextColor: '#fff',
        primaryBorderColor: '#00C4CC',
        lineColor: '#6C47FF',
        secondaryColor: '#6C47FF',
        tertiaryColor: '#f0f0f0',
      },
    });

    // Example Mermaid diagram - Bridge Architecture
    const diagramDefinition = `
      graph TB
        A[WeChat Users] -->|Message| B[WeChat Integration Layer]
        B -->|API| C[Message Processing Engine]
        C -->|Transform & Route| D[Zendesk API]
        D -->|Ticket Creation| E[Zendesk Platform]
        E -->|Response| D
        D -->|API| C
        C -->|Transform| B
        B -->|Message| A
        
        style A fill:#00C4CC,stroke:#00C4CC,color:#fff
        style B fill:#6C47FF,stroke:#6C47FF,color:#fff
        style C fill:#00C4CC,stroke:#00C4CC,color:#fff
        style D fill:#6C47FF,stroke:#6C47FF,color:#fff
        style E fill:#00C4CC,stroke:#00C4CC,color:#fff
    `;

    // Render diagram using mermaid.render (async)
    const renderDiagram = async () => {
      if (mermaidRef.current) {
        try {
          mermaidRef.current.innerHTML = '';
          const { svg } = await mermaid.render(diagramId, diagramDefinition);
          if (mermaidRef.current) {
            mermaidRef.current.innerHTML = svg;
          }
        } catch (error) {
          console.error('Mermaid rendering error:', error);
          if (mermaidRef.current) {
            mermaidRef.current.innerHTML = '<div class="text-red-500 p-4">Diagram rendering failed. Please refresh the page.</div>';
          }
        }
      }
    };

    renderDiagram();
  }, [diagramId]);

  return (
    <section
      id="solution"
      className="min-h-screen py-24 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.solution.title}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#00C4CC] to-[#6C47FF] mx-auto rounded-full"></div>
          <p className="text-xl text-gray-600 mt-6 max-w-3xl mx-auto">
            {t.solution.description}
          </p>
        </div>

        {/* Mermaid Diagram Container */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 md:p-12 shadow-2xl">
          <div
            ref={mermaidRef}
            className="mermaid flex items-center justify-center min-h-[400px]"
          >
            {/* Diagram will be rendered here by Mermaid */}
          </div>
        </div>

        {/* Real-World Experience Section */}
        <div className="mt-16 bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-8 md:p-12 shadow-xl">
          <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            {t.solution.realWorldTitle}
          </h3>
          <p className="text-lg text-gray-700 mb-6 leading-relaxed">
            {t.solution.realWorldDescription}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00C4CC] to-[#00C4CC] rounded-lg flex items-center justify-center mb-4">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{t.solution.completeIntegration.title}</h4>
              <p className="text-gray-600 text-sm">
                {t.solution.completeIntegration.description}
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="w-12 h-12 bg-gradient-to-br from-[#6C47FF] to-[#6C47FF] rounded-lg flex items-center justify-center mb-4">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{t.solution.socialFeatures.title}</h4>
              <p className="text-gray-600 text-sm">
                {t.solution.socialFeatures.description}
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00C4CC] to-[#6C47FF] rounded-lg flex items-center justify-center mb-4">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{t.solution.crossCultural.title}</h4>
              <p className="text-gray-600 text-sm">
                {t.solution.crossCultural.description}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;

